// modules/Battle/BattleActionHandler.js
// 战斗行动处理模块 - 处理玩家的攻击、技能、道具使用

import DebugHelper from '../DebugHelper.js';
import MessageHelper from '../MessageHelper.js';
import SettingsHelper from '../SettingsHelper.js';
import { playSceneSfx } from '../LazyAssetLoader.js';
import ItemSystem from '../ItemSystem.js';
import VehicleEquipmentSystem from '../VehicleEquipmentSystem.js';
import { GAME_CONSTANTS } from '../GameConstants.js';
import { checkIfMyTurn } from './BattleUtils.js';

export default class BattleActionHandler {
    constructor(scene, battlePlayer, battleEnemy, battleAnimation, battleUI, battleLogic, battleState) {
        this.scene = scene;
        this.battlePlayer = battlePlayer;
        this.battleEnemy = battleEnemy;
        this.battleAnimation = battleAnimation;
        this.battleUI = battleUI;
        this.battleLogic = battleLogic;
        this.battleState = battleState;
    }
    
    /**
     * 处理玩家行动
     * @param {string} action - 行动类型
     * @param {boolean} isTeamBattle - 是否是组队模式
     * @param {Object} teamMembers - 队伍成员
     * @param {number} currentTeamMemberIndex - 当前队员索引
     */
    handleAction(action, isTeamBattle = false, teamMembers = [], currentTeamMemberIndex = 0) {
        if (this.battleState.isAnimating) return;
        
        // 检查UI是否可交互
        if (this.battleUI && !this.battleUI.isInteractive()) {
            return;
        }
        
        // 检查是否是当前队员的回合
        if (isTeamBattle) {
            const turnCheck = checkIfMyTurn(isTeamBattle, teamMembers, currentTeamMemberIndex);
            if (!turnCheck.isMyTurn) {
                DebugHelper.log('不是你的回合，无法操作');
                this.battleUI.showMessage(`等待 ${turnCheck.currentMember?.playerName || '其他玩家'} 操作...`);
                return;
            }
        }
        
        DebugHelper.log('玩家行动:', action);
        
        switch(action) {
            case '攻击':
            case '战斗': // 兼容旧版本
                this.startAttack();
                break;
            case '防御':
                // 防御由 battle.js 的 handleDefense 方法处理
                return;
            case '技能':
                this.startSkillSelection();
                break;
            case '道具':
            case '工具': // 战车菜单中的工具
                this.startItemSelection();
                break;
            case '上车':
            case '下车':
                // 上/下车由 battle.js 的 handleBoardVehicle/handleExitVehicle 方法处理
                return;
            case '主炮':
                this.startWeaponSelection('mainCannon');
                break;
            case '副炮':
                this.startWeaponSelection('subCannon');
                break;
            case 'SE武器':
                this.startWeaponSelection('se');
                break;
            case '战术':
                this.startTacticSkill();
                break;
            case '逃跑':
            case '撤退': // 战车菜单中的撤退
                // 逃跑由BattleTurnManager处理
                return;
        }
    }
    
    /**
     * 开始攻击
     */
    startAttack() {
        const weapon = this.battlePlayer.getCurrentWeapon();
        
        if (weapon && weapon.range && weapon.range > 1) {
            // 范围攻击
            this.battleState.rangeAttackMode = true;
            this.battleUI.showRangeTargetSelection(weapon.range);
        } else {
            // 单目标攻击
            this.battleState.rangeAttackMode = false;
            this.battleUI.showTargetSelection();
        }
    }
    
    /**
     * 处理目标选择
     * @param {number} targetIndex - 目标索引
     * @param {boolean} isRangeAttack - 是否是范围攻击
     * @param {Function} onComplete - 完成回调
     */
    handleTargetSelection(targetIndex, isRangeAttack = false, onComplete = null) {
        if (this.battleState.isAnimating) return;

        if (this.battleState.focusFireMode) {
            this.battleState.focusFireMode = false;
            this.battleState.isAnimating = true;

            const target = this.battleEnemy.getEnemyByIndex(targetIndex);
            if (target && target.visible && target.enemyData) {
                this.battleEnemy.getAllEnemies().forEach((enemySprite) => {
                    if (enemySprite.enemyData) {
                        enemySprite.enemyData.focusMarked = false;
                    }
                });
                target.enemyData.focusMarked = true;
                this.battleUI.showMessage(`集火标记：${target.enemyData.name}`);
            } else {
                MessageHelper.showError(this.scene, '目标无效');
            }

            this.battleState.isAnimating = false;
            if (onComplete) onComplete();
            return;
        }
        
        this.battleState.isAnimating = true;
        
        // 如果选择了武器（战车武器），使用武器攻击
        if (this.battleState.selectedWeapon) {
            const target = this.battleEnemy.getEnemyByIndex(targetIndex);
            if (target && target.visible) {
                this.executeWeaponAttack(this.battleState.selectedWeapon, target, onComplete);
                this.battleState.selectedWeapon = null; // 清除选择的武器
                this.battleState.selectedWeaponSlot = null;
            } else {
                this.battleState.isAnimating = false;
                MessageHelper.showError(this.scene, '目标无效');
            }
            return;
        }
        
        // 如果选择了技能，使用技能攻击
        if (this.battleState.selectedSkill) {
            const target = this.battleEnemy.getEnemyByIndex(targetIndex);
            if (target && target.visible) {
                this.executeSkillAttack(this.battleState.selectedSkill, target, onComplete);
                this.battleState.selectedSkill = null; // 清除选择的技能
            } else {
                this.battleState.isAnimating = false;
                MessageHelper.showError(this.scene, '目标无效');
            }
            return;
        }
        
        // 如果选择了道具，使用道具攻击
        if (this.battleState.selectedItem) {
            const target = this.battleEnemy.getEnemyByIndex(targetIndex);
            if (target && target.visible) {
                this.executeItemAttack(this.battleState.selectedItem, target, onComplete);
            } else {
                this.battleState.isAnimating = false;
                MessageHelper.showError(this.scene, '目标无效');
            }
            return;
        }
        
        if (isRangeAttack) {
            // 范围攻击
            const targets = this.battleEnemy.getTargetsForRangeAttack(targetIndex, 2); // 假设范围2
            this.executeRangeAttack(targets, onComplete);
        } else {
            // 单目标攻击
            const target = this.battleEnemy.getEnemyByIndex(targetIndex);
            if (target && target.visible) {
                this.executeSingleAttack(target, onComplete);
            } else {
                this.battleState.isAnimating = false;
                MessageHelper.showError(this.scene, '目标无效');
            }
        }
    }
    
    /**
     * 执行单目标攻击
     * @param {Object} target - 目标敌人
     * @param {Function} onComplete - 完成回调
     * @param {Object} syncData - 同步数据（从网络接收时使用，包含预计算的伤害）
     */
    executeSingleAttack(target, onComplete = null, syncData = null) {
        const player = this.battlePlayer.getPlayer();
        
        // 如果有同步数据，使用同步的伤害；否则本地计算
        let damage = null;
        if (syncData && syncData.damage) {
            // 使用同步的伤害数据，确保格式正确
            damage = typeof syncData.damage === 'object' ? syncData.damage : {
                damage: syncData.damage,
                isCritical: syncData.damage.isCritical || false
            };
        } else {
            damage = this.battleLogic.calculatePlayerDamage(player, target.enemyData);
        }

        damage = this.applyFocusFireBonus(damage, target.enemyData);
        
        // 组队模式：发送动作同步消息（如果不是从网络接收的）
        if (!syncData && this.scene.isTeamBattle && window.networkManager && window.networkManager.isConnected()) {
            const targetIndex = target.index;
            const damageValue = typeof damage === 'object' ? damage.damage : damage;
            const weapon = (damage && damage.weapon) || this.battlePlayer.getCurrentWeapon();
            
            window.networkManager.notifyTeamBattleAction({
                action: 'attack',
                actionType: 'single',
                targetIndex: targetIndex,
                damage: damageValue,
                isCritical: damage.isCritical || false,
                weaponId: weapon ? (weapon.id || weapon.name) : null,
                animationConfig: weapon && weapon.animation ? weapon.animation : null
            });
        }

        // 检查是否有错误消息（例如没有弹药）
        if (damage && typeof damage === 'object' && damage.message) {
            MessageHelper.showError(this.scene, damage.message);
            this.battleState.isAnimating = false;
            if (onComplete) onComplete();
            return;
        }

        // 获取武器音效和动画配置
        // 如果有同步数据，使用同步的配置；否则从damage或当前武器获取
        let weapon = null;
        let weaponSound = null;
        let animationConfig = null;
        
        if (syncData) {
            // 使用同步的动画配置
            animationConfig = syncData.animationConfig || null;
            // 尝试从weaponId获取武器信息
            if (syncData.damage && syncData.damage.weapon) {
                weapon = syncData.damage.weapon;
            }
        }
        
        if (!weapon) {
            weapon = (damage && damage.weapon) || this.battlePlayer.getCurrentWeapon();
        }
        weaponSound = weapon ? weapon.sound : null;
        
        if (!animationConfig && weapon) {
            animationConfig = weapon.animation ? weapon.animation : null;
        }
        
        // 播放攻击音效：有武器用武器音效，没有武器用默认攻击音效
        this._playAttackSound(weaponSound);
        
        // 根据是否有武器选择不同的攻击动画
        if (weapon) {
            // 有武器：使用炮弹+爆炸动画
            this.battleAnimation.playAttackAnimation(
                this.battlePlayer.playerSprite,
                target,
                damage,
                () => {
                    // 动画完成回调
                    const actualDamage = damage.damage || damage;
                    this.battleLogic.applyDamage(target.enemyData, damage);
                    this.battleEnemy.updateEnemyHPBar(target);
                    
                    // 更新状态效果显示（如果应用了状态效果）
                    this.battleEnemy.updateEnemyStatusEffects(target);
                    
                    // 检查敌人是否死亡
                    if (target.enemyData.hp <= 0) {
                        target.setVisible(false);
                    }
                    
                    // 敌人受击抖动（如果伤害大于0），抖动完成后再检查战斗状态
                    if (actualDamage > 0) {
                        this.battleAnimation.playEnemyHitShake(target, actualDamage, () => {
                            // 抖动完成后再检查战斗状态
                            if (onComplete) onComplete();
                        });
                    } else {
                        // 没有伤害，直接检查战斗状态
                        if (onComplete) onComplete();
                    }
                },
                animationConfig
            );
        } else {
            // 无武器：使用默认攻击动画（角色抖动+敌人抖动）
            this.battleAnimation.playDefaultAttackAnimation(
                this.battlePlayer.playerSprite,
                target,
                damage,
                () => {
                    // 动画完成回调
                    const actualDamage = damage.damage || damage;
                    this.battleLogic.applyDamage(target.enemyData, damage);
                    this.battleEnemy.updateEnemyHPBar(target);
                    
                    // 更新状态效果显示（如果应用了状态效果）
                    this.battleEnemy.updateEnemyStatusEffects(target);
                    
                    // 检查敌人是否死亡
                    if (target.enemyData.hp <= 0) {
                        target.setVisible(false);
                    }
                    
                    // 敌人受击抖动完成后，延迟一段时间再检查战斗状态（与有武器攻击保持一致）
                    if (actualDamage > 0) {
                        // 延迟500ms，让玩家有足够时间看到反馈
                        this.scene.time.delayedCall(500, () => {
                            if (onComplete) onComplete();
                        });
                    } else {
                        // 没有伤害，直接检查战斗状态
                        if (onComplete) onComplete();
                    }
                }
            );
        }
    }
    
    /**
     * 执行范围攻击
     * @param {Array} targets - 目标敌人数组
     * @param {Function} onComplete - 完成回调
     */
    executeRangeAttack(targets, onComplete = null) {
        if (targets.length === 0) {
            this.battleState.isAnimating = false;
            return;
        }
        
        const player = this.battlePlayer.getPlayer();
        
        // 先检查第一个目标的伤害，如果有错误消息（如没有弹药），直接返回
        const firstTarget = targets.find(t => t && t.visible);
        let firstDamage = null;
        if (firstTarget) {
            firstDamage = this.battleLogic.calculatePlayerDamage(player, firstTarget.enemyData);
            if (firstDamage && typeof firstDamage === 'object' && firstDamage.message) {
                MessageHelper.showError(this.scene, firstDamage.message);
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
                return;
            }
        }
        
        let completedAttacks = 0;

        // 播放武器音效（只在第一次攻击时播放）
        // 优先使用firstDamage对象中的weapon（战车武器），如果没有再使用人类武器
        const weapon = (firstTarget && firstDamage && firstDamage.weapon) || this.battlePlayer.getCurrentWeapon();
        const weaponSound = weapon ? weapon.sound : null;
        
        // 获取武器动画配置
        const animationConfig = weapon && weapon.animation ? weapon.animation : null;
        
        // 播放攻击音效：有武器用武器音效，没有武器用默认攻击音效
        this._playAttackSound(weaponSound);
        
        // 跟踪抖动完成状态
        let completedShakes = 0;
        let totalShakes = 0; // 需要抖动的目标数量
        
        // 先计算需要抖动的目标数量
        targets.forEach((target) => {
            if (target && target.visible) {
                const damage = this.battleLogic.calculatePlayerDamage(player, target.enemyData);
                const actualDamage = damage.damage || damage;
                if (actualDamage > 0) {
                    totalShakes++;
                }
            }
        });
        
        targets.forEach((target, index) => {
            if (!target || !target.visible) {
                completedAttacks++;
                return;
            }
            
            const damage = this.battleLogic.calculatePlayerDamage(player, target.enemyData);
            
            // 延迟每个攻击的动画
            this.scene.time.delayedCall(index * 300, () => {
                this.battleAnimation.playRangeAttackAnimation(
                    this.battlePlayer.playerSprite,
                    target,
                    damage,
                    () => {
                        const actualDamage = damage.damage || damage;
                        this.battleLogic.applyDamage(target.enemyData, damage);
                        this.battleEnemy.updateEnemyHPBar(target);
                        this.battleAnimation.showDamage(target, damage);
                        
                        // 更新状态效果显示（如果应用了状态效果）
                        this.battleEnemy.updateEnemyStatusEffects(target);
                        
                        if (target.enemyData.hp <= 0) {
                            target.setVisible(false);
                        }
                        
                        completedAttacks++;
                        
                        // 敌人受击抖动（如果伤害大于0），等待所有抖动完成后再检查战斗状态
                        if (actualDamage > 0) {
                            this.battleAnimation.playEnemyHitShake(target, actualDamage, () => {
                                completedShakes++;
                                // 所有攻击和抖动都完成后，检查战斗状态
                                if (completedAttacks === targets.length && completedShakes === totalShakes) {
                                    if (onComplete) onComplete();
                                }
                            });
                        } else {
                            // 没有伤害，不需要抖动，但需要检查是否所有攻击和抖动都完成了
                            if (completedAttacks === targets.length && completedShakes === totalShakes) {
                                if (onComplete) onComplete();
                            }
                        }
                    },
                    animationConfig
                );
            });
        });
    }
    
    /**
     * 开始技能选择
     */
    startSkillSelection() {
        const player = this.battlePlayer.getPlayer();
        let skills = player.skills || [];
        
        // 如果在战车中，添加S-E技能
        if (player.inVehicle && player.vehicle) {
            const seSkills = this.battleLogic.getVehicleSESkills(player);
            skills = [...skills, ...seSkills];
        }
        
        if (skills.length === 0) {
            MessageHelper.showWarning(this.scene, '没有可用技能');
            return;
        }
        
        // 显示技能选择菜单
        this.battleUI.showSkillSelection(
            skills,
            player.cost || 0,
            player.maxCost || 100
        );
    }
    
    /**
     * 处理技能选择
     * @param {Object} skill - 技能对象
     * @param {number} skillIndex - 技能索引
     * @param {boolean} isTeamBattle - 是否是组队模式
     * @param {Object} teamMembers - 队伍成员
     * @param {number} currentTeamMemberIndex - 当前队员索引
     */
    handleSkillSelection(skill, skillIndex, isTeamBattle = false, teamMembers = [], currentTeamMemberIndex = 0) {
        if (this.battleState.isAnimating) return;

        if (this.battleState.selectingTactic) {
            this.battleState.selectingTactic = false;
            this.battleUI.hideSkillMenu();

            if (skill?.id === 'all_defend') {
                this.executeAllDefendTactic();
            } else if (skill?.id === 'focus_fire') {
                this.startFocusFireMark();
            }
            return;
        }
        
        // 检查是否是当前队员的回合
        if (isTeamBattle) {
            const turnCheck = checkIfMyTurn(isTeamBattle, teamMembers, currentTeamMemberIndex);
            if (!turnCheck.isMyTurn) {
                return;
            }
        }
        
        // 检查UI是否可交互
        if (this.battleUI && !this.battleUI.isInteractive()) {
            return;
        }
        
        const player = this.battlePlayer.getPlayer();
        
        // 检查是否是S-E技能
        if (skill.type === 'vehicle_se') {
            // S-E技能：使用战车S-E武器
            if (!player.inVehicle || !player.vehicle) {
                MessageHelper.showError(this.scene, '必须在战车中才能使用S-E技能！');
                return;
            }
            
            const vehicle = player.vehicle;
            const seWeapon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'se');
            
            if (!seWeapon) {
                MessageHelper.showError(this.scene, '战车没有装备S-E！');
                return;
            }
            
            // 检查弹药
            if (!VehicleEquipmentSystem.hasAmmo(seWeapon, vehicle)) {
                MessageHelper.showError(this.scene, `${seWeapon.name} 没有弹药！`);
                return;
            }
            
            // S-E技能：需要选择目标
            this.battleState.selectedSkill = skill;
            this.battleState.isAnimating = false;
            this.battleUI.hideSkillMenu();
            this.battleUI.showTargetSelection();
            return;
        }
        
        // 普通技能处理
        // 检查是否可以释放
        if (!this.battleLogic.canUseSkill(skill, player)) {
            MessageHelper.showError(this.scene, '无法使用该技能（Cost不足或条件不满足）');
            return;
        }
        
        // 消耗Cost
        if (!this.battleLogic.consumeSkillCost(skill, player)) {
            MessageHelper.showError(this.scene, 'Cost不足！');
            return;
        }
        
        // 根据技能类型执行
        if (skill.damage) {
            // 伤害技能：需要选择目标
            this.battleState.selectedSkill = skill;
            this.battleState.isAnimating = false;
            this.battleUI.hideSkillMenu();
            this.battleUI.showTargetSelection();
        } else if (skill.heal) {
            // 治疗技能：直接对玩家使用
            this.executeHealSkill(skill);
        } else {
            // 其他类型技能
            MessageHelper.showWarning(this.scene, '该技能类型暂未实现');
        }
    }
    
    /**
     * 执行治疗技能
     * @param {Object} skill - 技能对象
     * @param {Function} onComplete - 完成回调
     */
    executeHealSkill(skill, onComplete = null) {
        this.battleState.isAnimating = true;
        this.battleUI.hideSkillMenu();
        
        const player = this.battlePlayer.getPlayer();
        const skillEffect = this.battleLogic.calculateSkillEffect(skill, player);
        
        // 播放治疗动画
        this.battleAnimation.playHealAnimation(
            this.battlePlayer.playerSprite,
            skillEffect.heal,
            () => {
                // 应用治疗
                this.battlePlayer.heal(skillEffect.heal);
                MessageHelper.showSuccess(this.scene, `${skill.name} 恢复了 ${skillEffect.heal} 点生命值！`);
                
                // 检查战斗状态
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            }
        );
    }
    
    /**
     * 执行技能攻击
     * @param {Object} skill - 技能对象
     * @param {Object} target - 目标敌人
     * @param {Function} onComplete - 完成回调
     */
    executeSkillAttack(skill, target, onComplete = null) {
        if (!target || !target.visible) {
            this.battleState.isAnimating = false;
            return;
        }
        
        const player = this.battlePlayer.getPlayer();
        
        // 检查是否是S-E技能
        if (skill.type === 'vehicle_se' && player.inVehicle && player.vehicle) {
            // 使用战车S-E武器攻击
            const vehicle = player.vehicle;
            const seWeapon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'se');
            
            if (!seWeapon) {
                MessageHelper.showError(this.scene, 'S-E装备不存在！');
                this.battleState.isAnimating = false;
                return;
            }
            
            // 检查弹药
            if (!VehicleEquipmentSystem.hasAmmo(seWeapon, vehicle)) {
                MessageHelper.showError(this.scene, `${seWeapon.name} 没有弹药！`);
                this.battleState.isAnimating = false;
                return;
            }
            
            // 计算S-E伤害
            const damageInfo = this.battleLogic.calculateVehicleDamage(player, target.enemyData, 'se');
            
            if (damageInfo.message) {
                MessageHelper.showError(this.scene, damageInfo.message);
                this.battleState.isAnimating = false;
                return;
            }
            
            // 播放S-E攻击动画
            this.battleAnimation.playAttackAnimation(
                this.battlePlayer.playerSprite,
                target.enemySprite,
                () => {
                    // 应用伤害
                    this.battleEnemy.takeDamage(target.enemyData, damageInfo.damage, damageInfo.isCritical);
                    
                    // 显示伤害信息
                    const message = damageInfo.isCritical ? 
                        `暴击！${seWeapon.name} 造成 ${damageInfo.damage} 点伤害！` :
                        `${seWeapon.name} 造成 ${damageInfo.damage} 点伤害！`;
                    MessageHelper.showInfo(this.scene, message);
                    
                    // 检查敌人是否被击败
                    if (target.enemyData.hp <= 0) {
                        this.battleEnemy.defeatEnemy(target.enemyData);
                    }
                    
                    this.battleState.isAnimating = false;
                    if (onComplete) onComplete();
                }
            );
            return;
        }
        
        // 普通技能处理
        const skillEffect = this.battleLogic.calculateSkillEffect(skill, player, target.enemyData);
        
        // 播放技能攻击动画
        this.battleAnimation.playAttackAnimation(
            this.battlePlayer.playerSprite,
            target,
            skillEffect.damage,
            () => {
                // 应用伤害
                const actualDamage = skillEffect.damage || 0;
                this.battleLogic.applyDamage(target.enemyData, { damage: actualDamage });
                this.battleEnemy.updateEnemyHPBar(target);
                
                // 显示伤害
                this.battleAnimation.showDamage(target, actualDamage);
                
                // 更新状态效果显示（如果应用了状态效果）
                this.battleEnemy.updateEnemyStatusEffects(target);
                
                // 显示技能名称
                this.battleUI.showMessage(`${skill.name} 对 ${target.enemyData.name} 造成了 ${actualDamage} 点伤害！`);
                
                // 检查敌人是否死亡
                if (target.enemyData.hp <= 0) {
                    target.setVisible(false);
                }
                
                // 敌人受击抖动（如果伤害大于0），抖动完成后再检查战斗状态
                if (actualDamage > 0) {
                    this.battleAnimation.playEnemyHitShake(target, actualDamage, () => {
                        // 抖动完成后再检查战斗状态
                        if (onComplete) onComplete();
                    });
                } else {
                    // 没有伤害，直接检查战斗状态
                    if (onComplete) onComplete();
                }
            }
        );
    }
    
    /**
     * 开始道具选择
     */
    startItemSelection() {
        const player = this.battlePlayer.getPlayer();
        const inventory = player.inventory || [];
        
        if (inventory.length === 0) {
            this.battleUI.showMessage('背包中没有道具');
            return;
        }
        
        // 过滤出可以在战斗中使用的道具
        const usableItems = inventory.filter(item => {
            if (!item || item.type !== 'consumable') return false;
            return this.battleLogic.canUseItemInBattle(item, player);
        });
        
        if (usableItems.length === 0) {
            this.battleUI.showMessage('没有可在战斗中使用的道具');
            return;
        }
        
        // 统计道具数量
        const itemMap = new Map();
        usableItems.forEach(item => {
            const key = item.id || item.name;
            if (!itemMap.has(key)) {
                itemMap.set(key, { ...item, count: 0 });
            }
            itemMap.get(key).count++;
        });
        
        const itemList = Array.from(itemMap.values());
        
        // 显示道具选择菜单
        this.battleUI.showItemSelection(itemList);
    }
    
    /**
     * 处理道具选择
     * @param {Object} item - 道具对象
     * @param {number} itemIndex - 道具索引
     * @param {Function} onComplete - 完成回调
     */
    handleItemSelection(item, itemIndex, onComplete = null) {
        if (this.battleState.isAnimating) return;
        
        const player = this.battlePlayer.getPlayer();
        
        // 检查是否可以使用的道具
        if (!this.battleLogic.canUseItemInBattle(item, player)) {
            this.battleUI.showMessage('无法使用该道具');
            return;
        }
        
        // 根据道具类型执行
        if (item.damage) {
            // 伤害道具：需要选择目标
            this.battleState.selectedItem = item;
            this.battleState.isAnimating = false;
            this.battleUI.hideItemMenu();
            this.battleUI.showTargetSelection();
        } else {
            // 恢复/修复道具：直接使用
            this.executeItemUse(item, onComplete);
        }
    }
    
    /**
     * 执行道具使用
     * @param {Object} item - 道具对象
     * @param {Function} onComplete - 完成回调
     */
    executeItemUse(item, onComplete = null) {
        this.battleState.isAnimating = true;
        this.battleUI.hideItemMenu();
        
        const player = this.battlePlayer.getPlayer();
        
        // 使用ItemSystem使用道具
        const result = ItemSystem.useItem(item, player);
        
        if (result.success) {
            // 从背包中移除道具
            if (item.type === 'consumable') {
                ItemSystem.removeFromInventory(item, player.inventory);
            }
            
            // 根据效果类型播放动画
            if (result.effect.healed) {
                this.battleAnimation.playHealAnimation(
                    this.battlePlayer.playerSprite,
                    result.effect.healed,
                    () => {
                        this.battlePlayer.updatePlayerHPBar();
                        this.battleUI.showMessage(result.message);
                        this.battleState.isAnimating = false;
                        if (onComplete) onComplete();
                    }
                );
            } else if (result.effect.repaired) {
                this.battleUI.showMessage(result.message);
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            } else {
                this.battleUI.showMessage(result.message);
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            }
        } else {
            this.battleUI.showMessage(result.message);
            this.battleState.isAnimating = false;
        }
    }
    
    /**
     * 执行道具攻击
     * @param {Object} item - 道具对象
     * @param {Object} target - 目标敌人
     * @param {Function} onComplete - 完成回调
     */
    executeItemAttack(item, target, onComplete = null) {
        if (!target || !target.visible) {
            this.battleState.isAnimating = false;
            return;
        }
        
        const player = this.battlePlayer.getPlayer();
        const damage = item.damage || 0;
        
        // 播放道具攻击动画
        this.battleAnimation.playAttackAnimation(
            this.battlePlayer.playerSprite,
            target,
            damage,
            () => {
                // 应用伤害
                const actualDamage = damage || 0;
                this.battleLogic.applyDamage(target.enemyData, { damage: actualDamage });
                this.battleEnemy.updateEnemyHPBar(target);
                
                // 显示伤害
                this.battleAnimation.showDamage(target, actualDamage);
                
                // 更新状态效果显示（如果应用了状态效果）
                this.battleEnemy.updateEnemyStatusEffects(target);
                
                // 显示道具使用消息
                this.battleUI.showMessage(`使用 ${item.name} 对 ${target.enemyData.name} 造成了 ${actualDamage} 点伤害！`);
                
                // 从背包中移除道具
                ItemSystem.removeFromInventory(item, player.inventory);
                
                // 检查敌人是否死亡
                if (target.enemyData.hp <= 0) {
                    target.setVisible(false);
                }
                
                // 清除选择的道具
                this.battleState.selectedItem = null;
                
                // 敌人受击抖动（如果伤害大于0），抖动完成后再检查战斗状态
                if (actualDamage > 0) {
                    this.battleAnimation.playEnemyHitShake(target, actualDamage, () => {
                        // 抖动完成后再检查战斗状态
                        if (onComplete) onComplete();
                    });
                } else {
                    // 没有伤害，直接检查战斗状态
                    if (onComplete) onComplete();
                }
            }
        );
    }
    
    /**
     * 取消目标选择
     */
    cancelTargetSelection() {
        this.battleState.rangeAttackMode = false;
        this.battleState.focusFireMode = false;
        this.battleUI.showPlayerTurn();
        this.battleState.isAnimating = false;
    }
    
    /**
     * 取消技能选择
     */
    cancelSkillSelection() {
        this.battleState.selectedSkill = null;
        this.battleState.selectingTactic = false;
        this.battleUI.showPlayerTurn();
    }
    
    /**
     * 取消道具选择
     */
    cancelItemSelection() {
        this.battleState.selectedItem = null;
        this.battleUI.showPlayerTurn();
    }
    
    /**
     * 开始防御
     */
    startDefense() {
        const player = this.battlePlayer.getPlayer();
        
        // 如果是多人游戏，需要选择防御目标（自己或队友）
        // TODO: 实现多人游戏的防御目标选择
        // 暂时先防御自己
        this.executeDefense(player);
    }
    
    /**
     * 执行防御
     * @param {Object} target - 防御目标（玩家或队友）
     * @param {Function} onComplete - 完成回调
     * @param {Object} syncData - 同步数据（从网络接收时使用）
     */
    executeDefense(target, onComplete = null, syncData = null) {
        this.battleState.isAnimating = true;
        
        // 组队模式：发送防御动作同步消息（如果不是从网络接收的）
        if (!syncData && this.scene.isTeamBattle && window.networkManager && window.networkManager.isConnected()) {
            window.networkManager.notifyTeamBattleAction({
                action: 'defense',
                targetPlayerId: target.playerId || null
            });
        }
        
        // 设置防御状态（下一回合受到的伤害减少）
        target.defending = true;
        target.defenseBonus = 0.5; // 减少50%伤害
        
        // 显示防御动画
        this.battleAnimation.playDefenseAnimation(
            this.battlePlayer.playerSprite,
            () => {
                this.battleUI.showMessage(`${target.name || '玩家'} 进入防御状态！`);
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            }
        );
    }
    
    /**
     * 开始上车
     */
    startBoardVehicle() {
        const player = this.battlePlayer.getPlayer();
        
        if (player.inVehicle) {
            MessageHelper.showWarning(this.scene, '已经在战车中！');
            return;
        }
        
        if (!player.vehicle) {
            MessageHelper.showError(this.scene, '没有战车！');
            return;
        }
        
        // 检查战车是否可以上车
        const canBoard = VehicleEquipmentSystem.canBoard(player.vehicle);
        
        if (!canBoard.canBoard) {
            MessageHelper.showError(this.scene, canBoard.message);
            return;
        }
        
        // 执行上车
        this.executeBoardVehicle();
    }
    
    /**
     * 执行上车
     * @param {Function} onComplete - 完成回调
     */
    executeBoardVehicle(onComplete = null) {
        this.battleState.isAnimating = true;
        const player = this.battlePlayer.getPlayer();
        
        player.inVehicle = true;
        
        // 更新玩家精灵外观（显示战车）
        this.battlePlayer.updatePlayerAppearance();
        
        // 更新菜单
        this.battleUI.menu.updateMenuOptions();
        
        // 显示上车动画
        this.battleAnimation.playBoardVehicleAnimation(
            this.battlePlayer.playerSprite,
            () => {
                this.battleUI.showMessage('进入战车！');
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            }
        );
    }
    
    /**
     * 开始下车
     */
    startExitVehicle() {
        const player = this.battlePlayer.getPlayer();
        
        if (!player.inVehicle) {
            MessageHelper.showWarning(this.scene, '不在战车中！');
            return;
        }
        
        // 执行下车
        this.executeExitVehicle();
    }
    
    /**
     * 执行下车
     * @param {Function} onComplete - 完成回调
     */
    executeExitVehicle(onComplete = null) {
        this.battleState.isAnimating = true;
        const player = this.battlePlayer.getPlayer();
        
        player.inVehicle = false;
        
        // 更新玩家精灵外观（显示人类）
        this.battlePlayer.updatePlayerAppearance();
        
        // 更新菜单
        this.battleUI.menu.updateMenuOptions();
        
        // 显示下车动画
        this.battleAnimation.playExitVehicleAnimation(
            this.battlePlayer.playerSprite,
            () => {
                this.battleUI.showMessage('离开战车！');
                this.battleState.isAnimating = false;
                if (onComplete) onComplete();
            }
        );
    }
    
    /**
     * 开始武器选择（战车武器）
     * @param {string} weaponType - 武器类型 ('mainCannon', 'subCannon', 'se')
     */
    startWeaponSelection(weaponType) {
        const player = this.battlePlayer.getPlayer();
        
        if (!player.inVehicle || !player.vehicle) {
            MessageHelper.showError(this.scene, '必须在战车中才能使用武器！');
            return;
        }
        
        const vehicle = player.vehicle;
        
        // 获取该类型的所有武器（可能有多个）
        const weapons = [];
        
        // 检查主炮
        if (weaponType === 'mainCannon') {
            const mainCannon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'mainCannon');
            if (mainCannon) {
                weapons.push({ ...mainCannon, slot: 'mainCannon' });
            }
        }
        
        // 检查副炮
        if (weaponType === 'subCannon') {
            const subCannon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'subCannon');
            if (subCannon) {
                weapons.push({ ...subCannon, slot: 'subCannon' });
            }
        }
        
        // 检查SE武器
        if (weaponType === 'se') {
            const seWeapon = VehicleEquipmentSystem.getEquippedItem(vehicle, 'se');
            if (seWeapon) {
                weapons.push({ ...seWeapon, slot: 'se' });
            }
        }

        DebugHelper.log('可用武器列表:', weapons);
        
        if (weapons.length === 0) {
            MessageHelper.showWarning(this.scene, `没有装备${weaponType === 'mainCannon' ? '主炮' : weaponType === 'subCannon' ? '副炮' : 'SE武器'}！`);
            return;
        }
        
        // 如果只有一个武器，直接使用
        if (weapons.length === 1) {
            const weapon = weapons[0];
            // 检查弹药
            if (!VehicleEquipmentSystem.hasAmmo(weapon, vehicle)) {
                MessageHelper.showWarning(this.scene, `${weapon.name} 没有弹药！`);
                return;
            }
            this.battleState.selectedWeapon = weapon;
            this.battleState.selectedWeaponSlot = weaponType;
            // 检查是否是范围攻击
            if (weapon.range && weapon.range > 1) {
                this.battleState.rangeAttackMode = true;
                this.battleUI.showRangeTargetSelection(weapon.range);
            } else {
                this.battleState.rangeAttackMode = false;
                this.battleUI.showTargetSelection();
            }
            return;
        }
        
        // 多个武器，显示选择菜单
        this.battleUI.showWeaponSelection(weapons, weaponType);
    }
    
    /**
     * 处理武器选择
     * @param {Object} weapon - 武器对象
     * @param {number} weaponIndex - 武器索引
     * @param {string} weaponType - 武器类型
     */
    handleWeaponSelection(weapon, weaponIndex, weaponType) {
        if (this.battleState.isAnimating) return;
        
        const player = this.battlePlayer.getPlayer();
        
        if (!player.inVehicle || !player.vehicle) {
            MessageHelper.showError(this.scene, '必须在战车中才能使用武器！');
            return;
        }
        
        // 检查弹药
        if (!VehicleEquipmentSystem.hasAmmo(weapon, player.vehicle)) {
            MessageHelper.showError(this.scene, `${weapon.name} 没有弹药！`);
            return;
        }
        
        // 保存选择的武器
        this.battleState.selectedWeapon = weapon;
        this.battleState.selectedWeaponSlot = weaponType;
        
        // 检查是否是范围攻击
        if (weapon.range && weapon.range > 1) {
            this.battleState.rangeAttackMode = true;
            this.battleUI.hideWeaponMenu();
            this.battleUI.showRangeTargetSelection(weapon.range);
        } else {
            this.battleState.rangeAttackMode = false;
            this.battleUI.hideWeaponMenu();
            this.battleUI.showTargetSelection();
        }
    }
    
    /**
     * 开始战术技能
     */
    startTacticSkill() {
        const player = this.battlePlayer.getPlayer();
        
        if (!player.inVehicle || !player.vehicle) {
            MessageHelper.showError(this.scene, '必须在战车中才能使用战术！');
            return;
        }
        
        const tactics = [
            {
                id: 'all_defend',
                name: '全体防御',
                cost: 0,
                description: '全队进入防御姿态1回合'
            },
            {
                id: 'focus_fire',
                name: '集火',
                cost: 0,
                description: '标记目标，对其伤害+25%'
            }
        ];
        
        this.battleState.selectingTactic = true;
        this.battleUI.showSkillSelection(tactics, player.cost || 0, player.maxCost || 100);
    }

    applyFocusFireBonus(damageInfo, enemyData) {
        if (!enemyData?.focusMarked || damageInfo == null) {
            return damageInfo;
        }

        if (typeof damageInfo === 'number') {
            return Math.floor(damageInfo * GAME_CONSTANTS.BATTLE.FOCUS_FIRE_DAMAGE_MULTIPLIER);
        }

        if (typeof damageInfo !== 'object') {
            return damageInfo;
        }

        return {
            ...damageInfo,
            damage: Math.floor((damageInfo.damage || 0) * GAME_CONSTANTS.BATTLE.FOCUS_FIRE_DAMAGE_MULTIPLIER)
        };
    }

    executeAllDefendTactic() {
        if (this.scene.isTeamBattle && this.scene.teamMembers) {
            this.scene.teamMembers.forEach((member) => {
                if (member.playerData) {
                    member.playerData.defending = true;
                    member.playerData.defenseBonus = 0.5;
                }
            });
        }

        this.battleUI.showMessage('全队进入防御姿态！');
        this.scene.handleDefense();
    }

    startFocusFireMark() {
        this.battleState.focusFireMode = true;
        this.battleUI.showTargetSelection();
    }
    
    /**
     * 执行武器攻击（战车武器）
     * @param {Object} weapon - 武器对象
     * @param {Object} target - 目标敌人
     * @param {Function} onComplete - 完成回调
     */
    executeWeaponAttack(weapon, target, onComplete = null) {
        if (!target || !target.visible) {
            this.battleState.isAnimating = false;
            return;
        }
        
        const player = this.battlePlayer.getPlayer();
        
        if (!player.inVehicle || !player.vehicle) {
            MessageHelper.showError(this.scene, '必须在战车中才能使用武器！');
            this.battleState.isAnimating = false;
            return;
        }
        
        // 计算伤害
        const damageInfo = this.battleLogic.calculateVehicleDamage(player, target.enemyData, weapon.slot);
        const damageInfoWithFocus = this.applyFocusFireBonus(damageInfo, target.enemyData);
        
        if (damageInfoWithFocus.message) {
            MessageHelper.showError(this.scene, damageInfoWithFocus.message);
            this.battleState.isAnimating = false;
            if (onComplete) onComplete();
            return;
        }
        
        // 获取武器音效和动画配置
        const weaponSound = weapon.sound || null;
        const animationConfig = weapon.animation || null;
        
        // 播放攻击音效
        this._playAttackSound(weaponSound);
        
        // 播放攻击动画
        this.battleAnimation.playAttackAnimation(
            this.battlePlayer.playerSprite,
            target,
            damageInfoWithFocus,
            () => {
                const actualDamage = damageInfoWithFocus.damage || 0;
                this.battleLogic.applyDamage(target.enemyData, damageInfoWithFocus);
                this.battleEnemy.updateEnemyHPBar(target);
                
                // 更新状态效果显示
                this.battleEnemy.updateEnemyStatusEffects(target);
                
                // 显示伤害信息
                const message = damageInfoWithFocus.isCritical ? 
                    `暴击！${weapon.name} 造成 ${actualDamage} 点伤害！` :
                    `${weapon.name} 造成 ${actualDamage} 点伤害！`;
                this.battleUI.showMessage(message);
                
                // 检查敌人是否死亡
                if (target.enemyData.hp <= 0) {
                    target.setVisible(false);
                }
                
                // 敌人受击抖动
                if (actualDamage > 0) {
                    this.battleAnimation.playEnemyHitShake(target, actualDamage, () => {
                        if (onComplete) onComplete();
                    });
                } else {
                    if (onComplete) onComplete();
                }
            },
            animationConfig
        );
    }
    
    /**
     * 取消武器选择
     */
    cancelWeaponSelection() {
        this.battleState.selectedWeapon = null;
        this.battleState.selectedWeaponSlot = null;
        this.battleUI.showPlayerTurn();
    }
    
    /**
     * 播放攻击音效（提取重复代码）
     * @param {string|null} weaponSound - 武器音效键名，如果为null则播放默认攻击音效
     * @private
     */
    _playAttackSound(weaponSound) {
        const soundVolume = SettingsHelper.getSoundPlayVolume(0.3);
        playSceneSfx(this.scene, weaponSound || 'default_attack', { volume: soundVolume });
    }
}

